import { useEffect, useMemo, useState } from "react"
import { Plus, Ship } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { FilterChip } from "@/components/filter-chip"
import { LoadingState } from "@/components/loading-state"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useContracts } from "@/features/contracts/hooks/use-contracts"
import { NewOTDialog } from "@/features/transit/components/new-ot-dialog/new-ot-dialog"
import { TransitRow } from "@/features/transit/components/transit-row"
import { useTransitOrders } from "@/features/transit/hooks/use-transit-orders"
import { NEW_OT_LOT_STATE_KEY } from "@/features/transit/lib/new-ot-state"
import type {
  ListOTsParams,
  OTListTab,
} from "@/features/transit/api/types"

const TABS: OTListTab[] = ["in_progress", "all", "completed"]

export function TransitOrdersPage() {
  const { t } = useTranslation("transit")
  const location = useLocation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<OTListTab>("in_progress")
  const [contractFilter, setContractFilter] = useState<string>("all")

  // The lot detail page navigates here with `state.newOTLotId` set when
  // the user clicked "Create new OT" — open the wizard pre-seeded and
  // clear the state so a refresh / back-nav doesn't reopen it.
  const seededLotId =
    typeof location.state === "object" && location.state !== null
      ? ((location.state as Record<string, unknown>)[NEW_OT_LOT_STATE_KEY] as
          | number
          | undefined)
      : undefined
  const [wizardOpen, setWizardOpen] = useState(seededLotId != null)
  const [wizardLotIds, setWizardLotIds] = useState<number[] | undefined>(
    seededLotId != null ? [seededLotId] : undefined,
  )

  useEffect(() => {
    if (seededLotId != null) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // Run once per arrival; subsequent state changes shouldn't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const openBlankWizard = () => {
    setWizardLotIds(undefined)
    setWizardOpen(true)
  }

  const params = useMemo<ListOTsParams>(() => {
    const out: ListOTsParams = { tab }
    if (contractFilter !== "all") out.contractId = contractFilter
    return out
  }, [tab, contractFilter])

  const { data, isLoading } = useTransitOrders(params)
  const list = data ?? []
  const { data: contracts } = useContracts()

  const inProgressCount =
    tab === "in_progress" ? list.length : (data ?? []).length
  const completedCount = tab === "completed" ? list.length : 0

  const contractOptions = (contracts ?? []).map((c) => ({
    value: String(c.id),
    label:
      c.contractNumber || c.clientName
        ? `${c.contractNumber ?? "—"}${
            c.clientName ? ` · ${c.clientName}` : ""
          }`
        : `#${c.id}`,
  }))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t("list.eyebrow")}
        title={t("list.title")}
        description={
          inProgressCount > 0
            ? t("list.subtitle", {
                count: inProgressCount,
                completed: completedCount,
              })
            : t("list.subtitleIdle", { completed: completedCount })
        }
        actions={
          <Button size="sm" onClick={openBlankWizard}>
            <Plus className="size-4" />
            {t("list.newOrder")}
          </Button>
        }
      />

      <Card className="flex flex-col gap-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as OTListTab)}>
            <TabsList variant="line" className="justify-start gap-6">
              {TABS.map((key) => (
                <TabsTrigger key={key} value={key}>
                  {t(`list.tabs.${key}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <FilterChip
            label={t("list.filterContract")}
            value={contractFilter}
            onValueChange={setContractFilter}
            options={contractOptions}
            allLabel={t("list.filterAllContracts")}
          />
        </div>

        {isLoading ? (
          <LoadingState />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Ship className="size-7" />}
            title={
              tab === "completed"
                ? t("list.empty.completed")
                : tab === "all"
                  ? t("list.empty.all")
                  : t("list.empty.title")
            }
            description={
              tab === "in_progress" ? t("list.empty.body") : undefined
            }
            action={
              tab !== "completed" ? (
                <Button size="sm" onClick={openBlankWizard}>
                  <Plus className="size-4" />
                  {t("list.empty.createCta")}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="flex flex-col gap-2">
            {list.map((ot) => (
              <TransitRow key={ot.id} ot={ot} />
            ))}
          </div>
        )}
      </Card>

      <NewOTDialog
        key={(wizardLotIds ?? []).join(",") || "blank"}
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        initialLotIds={wizardLotIds}
      />
    </div>
  )
}
